/**
 * paymentMethodUsageFee.ts — Tarifas de uso por método de pago (tarjeta / digital).
 *
 * Qué hace:
 *   Calcula cobrado vs neto ingreso según quién paga la comisión (negocio o cliente).
 *   Enriquece líneas de pago al cobrar y agrega totales para cierre de caja.
 *
 * Reglas:
 *   - negocio: el cliente paga el monto de la venta; la pasarela descuenta la tarifa.
 *     neto = cobrado − tarifa.
 *   - cliente: se recarga al cliente sobre la base; el negocio recibe la base (neto).
 *     cobrado = base + tarifa, neto = base.
 *
 * NO debe: importar React, leer localStorage ni mutar ventas.
 */

import type { PaymentBreakdown, Venta } from "../types/caja.types";
import type {
  PaymentMethodSetting,
  PaymentMethodsConfig,
  UsageFeePaidBy,
} from "@/lib/paymentMethodsConfig";
import { getPaymentMethodById } from "@/lib/paymentMethodsConfig";
import {
  extractPaymentLinesFromVenta,
  slotToLegacyBucket,
} from "./paymentMethodAggregation";
import type { MontosPorMetodoPago } from "./propinaAllocation";

export type { UsageFeePaidBy } from "@/lib/paymentMethodsConfig";

export interface UsageFeeConfig {
  pct: number;
  paidBy: UsageFeePaidBy;
}

export interface UsageFeeSnapshot {
  usageFeePct: number;
  usageFeePaidBy: UsageFeePaidBy;
  usageFeeAmount: number;
  netAmount: number;
}

const EMPTY: MontosPorMetodoPago = {
  efectivo: 0,
  nequi: 0,
  daviplata: 0,
  tarjeta: 0,
};

function roundCop(n: number): number {
  return Math.max(0, Math.round(n));
}

function clampPct(raw: unknown): number {
  const n = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(100, Math.round(n * 100) / 100);
}

/** Normaliza config parcial guardada en el método de pago. */
export function normalizeUsageFeeConfig(
  raw: PaymentMethodSetting["usageFee"],
): UsageFeeConfig | null {
  if (!raw || typeof raw !== "object") return null;
  const pct = clampPct(raw.pct);
  if (pct <= 0) return null;
  const paidBy: UsageFeePaidBy =
    raw.paidBy === "cliente" ? "cliente" : "negocio";
  return { pct, paidBy };
}

/** Tarifa aplicable solo a tarjeta y digitales (nunca efectivo). */
export function getUsageFeeForMethod(
  methodId: string,
  cfg?: PaymentMethodsConfig | null,
): UsageFeeConfig | null {
  if (!methodId || methodId === "efectivo") return null;
  const setting = cfg ? getPaymentMethodById(cfg, methodId) : undefined;
  if (!setting || setting.kind === "efectivo") return null;
  return normalizeUsageFeeConfig(setting.usageFee);
}

/** Recargo sobre la base cuando la paga el cliente (gross-up exacto).
 *
 * La pasarela cobra su % sobre el total FINAL que el cliente envía. Si el
 * recargo fuera un simple % de la base (cobrado = base × (1 + %)), la tarifa
 * real quedaría corta: base de $14.000 con 1,5% → cobrar $14.210, la pasarela
 * descuenta 1,5% de $14.210 = $213,15 y el negocio pierde $3,15 (el clásico
 * «impuesto sobre impuesto»: base × %²).
 *
 * Solución: el recargo se calcula sobre la base neta que debe quedar al negocio:
 *   fee = ceil(base × % / (100 − %));  cobrado = base + fee
 * Ej.: $14.000 + 1,5% → fee = ceil(14.000×1,5/98,5) = $214 → cobrado $14.214.
 * La pasarela descuenta ≈$213–214 y al negocio le quedan $14.000 o $1 más:
 * SIEMPRE su base, nunca menos (se usa ceil para absorber el redondeo).
 */
export function calcClienteFeeFromBase(
  baseAmount: number,
  pct: number,
): { feeAmount: number; grossAmount: number } {
  const base = roundCop(baseAmount);
  if (base <= 0 || pct <= 0) {
    return { feeAmount: 0, grossAmount: base };
  }
  const pctBps = Math.round(pct * 100);
  if (pctBps >= 10000) {
    const feeAmount = roundCop((base * pct) / 100);
    return { feeAmount, grossAmount: base + feeAmount };
  }
  const feeAmount = Math.ceil((base * pctBps) / (10000 - pctBps));
  return { feeAmount, grossAmount: base + feeAmount };
}

/** Tarifa descontada del cobrado cuando la paga el negocio. */
export function calcNegocioFeeFromGross(
  grossAmount: number,
  pct: number,
): { feeAmount: number; netAmount: number } {
  const gross = roundCop(grossAmount);
  if (gross <= 0 || pct <= 0) {
    return { feeAmount: 0, netAmount: gross };
  }
  const feeAmount = roundCop((gross * pct) / 100);
  return { feeAmount, netAmount: gross - feeAmount };
}

/** Infiere base desde cobrado cuando la tarifa la pagó el cliente.
 *
 * Inversa exacta del gross-up (`calcClienteFeeFromBase`): para un cobrado
 * generado con la fórmula nueva devuelve la base exacta que se recargó.
 *   neto = floor(cobrado × (100 − %) / 100) con aritmética entera.
 * En cobrados antiguos (snapshot legacy con recargo simple) el neto puede
 * variar ±1 peso, pero nunca es mayor al cobrado.
 */
export function calcClienteBaseFromGross(
  grossAmount: number,
  pct: number,
): { feeAmount: number; netAmount: number } {
  const gross = roundCop(grossAmount);
  if (gross <= 0 || pct <= 0) {
    return { feeAmount: 0, netAmount: gross };
  }
  const pctBps = Math.round(pct * 100);
  if (pctBps >= 10000) {
    const netAmount = roundCop(gross / (1 + pct / 100));
    const feeAmount = gross - netAmount;
    return { feeAmount, netAmount };
  }
  const netAmount = Math.floor((gross * (10000 - pctBps)) / 10000);
  const feeAmount = gross - netAmount;
  return { feeAmount, netAmount };
}

/** Snapshot contable de una línea según config vigente al cobrar. */
export function buildUsageFeeSnapshot(
  methodId: string,
  grossAmount: number,
  cfg?: PaymentMethodsConfig | null,
): UsageFeeSnapshot | null {
  const feeCfg = getUsageFeeForMethod(methodId, cfg);
  if (!feeCfg) return null;

  const gross = roundCop(grossAmount);
  if (gross <= 0) return null;

  if (feeCfg.paidBy === "negocio") {
    const { feeAmount, netAmount } = calcNegocioFeeFromGross(gross, feeCfg.pct);
    return {
      usageFeePct: feeCfg.pct,
      usageFeePaidBy: "negocio",
      usageFeeAmount: feeAmount,
      netAmount,
    };
  }

  const { feeAmount, netAmount } = calcClienteBaseFromGross(gross, feeCfg.pct);
  return {
    usageFeePct: feeCfg.pct,
    usageFeePaidBy: "cliente",
    usageFeeAmount: feeAmount,
    netAmount,
  };
}

export interface PaymentLineWithFeeInput {
  method: string;
  amount: number;
  received?: number;
  methodLabel?: string;
}

/** Aplica recargo al cliente sobre líneas digitales/tarjeta (aumenta el cobrado). */
export function applyClienteSurchargesToLines(
  lines: PaymentLineWithFeeInput[],
  cfg?: PaymentMethodsConfig | null,
): PaymentLineWithFeeInput[] {
  return lines.map((line) => {
    const feeCfg = getUsageFeeForMethod(line.method, cfg);
    if (!feeCfg || feeCfg.paidBy !== "cliente") return line;
    const base = roundCop(line.amount);
    if (base <= 0) return line;
    const { grossAmount } = calcClienteFeeFromBase(base, feeCfg.pct);
    return { ...line, amount: grossAmount };
  });
}

/** Enriquece breakdown persistido con snapshot de tarifa (idempotente si ya existe). */
export function enrichBreakdownWithUsageFees(
  breakdown: PaymentBreakdown[],
  cfg?: PaymentMethodsConfig | null,
): PaymentBreakdown[] {
  return breakdown.map((line) => {
    if (
      line.usageFeeAmount != null &&
      line.netAmount != null &&
      line.usageFeePct != null
    ) {
      return line;
    }
    const snap = buildUsageFeeSnapshot(line.method, line.amount, cfg);
    if (!snap) return line;
    return { ...line, ...snap };
  });
}

export function lineHasUsageFee(line: PaymentBreakdown): boolean {
  return (
    typeof line.usageFeeAmount === "number" &&
    line.usageFeeAmount > 0 &&
    typeof line.netAmount === "number"
  );
}

/** Neto que ingresa al negocio por línea (sin tarifa = cobrado). */
export function resolveLineNetAmount(line: PaymentBreakdown): number {
  if (lineHasUsageFee(line)) return roundCop(line.netAmount as number);
  return roundCop(line.amount);
}

/** Cobrado bruto de la línea. */
export function resolveLineGrossAmount(line: PaymentBreakdown): number {
  return roundCop(line.amount);
}

function addToBucket(
  out: MontosPorMetodoPago,
  slotId: string,
  gross: number,
  net: number,
  fee: number,
  mode: "gross" | "net" | "fee",
): void {
  const bucket = slotToLegacyBucket(slotId);
  if (bucket === "otros") return;
  if (mode === "gross") out[bucket] += gross;
  else if (mode === "net") out[bucket] += net;
  else out[bucket] += fee;
}

/** Suma tarifas de uso por bucket legacy (solo ventas activas). */
export function sumTarifasUsoPorMetodo(ventas: Venta[]): MontosPorMetodoPago {
  const out = { ...EMPTY };
  ventas.forEach((v) => {
    if (v.estado === "anulada") return;
    const lines = v.detalleMetodoPago?.breakdown?.length
      ? v.detalleMetodoPago.breakdown
      : null;

    if (lines?.length) {
      lines.forEach((line) => {
        if (!lineHasUsageFee(line)) return;
        addToBucket(
          out,
          line.method,
          line.amount,
          line.netAmount as number,
          line.usageFeeAmount as number,
          "fee",
        );
      });
      return;
    }

    if (
      v.metodoPago &&
      v.metodoPago !== "mixto" &&
      v.metodoPago !== "credito"
    ) {
      const pseudo: PaymentBreakdown = {
        method: v.metodoPago,
        amount: v.total,
      };
      if (!lineHasUsageFee(pseudo)) return;
    }
  });
  return out;
}

/** Neto real ingresado al negocio por método (post tarifa de pasarela). */
export function sumNetoIngresoPorMetodo(ventas: Venta[]): MontosPorMetodoPago {
  const out = { ...EMPTY };
  ventas.forEach((v) => {
    if (v.estado === "anulada") return;

    const breakdown = v.detalleMetodoPago?.breakdown;
    if (breakdown?.length) {
      breakdown.forEach((line) => {
        if ((line.amount || 0) <= 0) return;
        const net = lineHasUsageFee(line)
          ? resolveLineNetAmount(line)
          : roundCop(line.amount);
        addToBucket(out, line.method, line.amount, net, 0, "net");
      });
      return;
    }

    if (
      !v.metodoPago ||
      v.metodoPago === "mixto" ||
      v.metodoPago === "credito"
    ) {
      return;
    }

    const gross = roundCop(v.total);
    if (gross <= 0) return;
    addToBucket(out, v.metodoPago, gross, gross, 0, "net");
  });
  return out;
}

/** Total de tarifas de uso del turno. */
export function totalTarifasUso(montos: MontosPorMetodoPago): number {
  return montos.efectivo + montos.nequi + montos.daviplata + montos.tarjeta;
}

/** ¿Alguna venta del turno registró tarifas de uso? */
export function turnoTieneTarifasUso(ventas: Venta[]): boolean {
  return ventas.some((v) => {
    if (v.estado === "anulada") return false;
    const lines = v.detalleMetodoPago?.breakdown;
    if (lines?.some(lineHasUsageFee)) return true;
    return false;
  });
}

/** Monto neto por línea extraída de venta (cuadre transferencias). */
export function netAmountFromPaymentLine(
  venta: Venta,
  slotId: string,
  gross: number,
  breakdownIndex?: number,
): number {
  const breakdown = venta.detalleMetodoPago?.breakdown;
  if (breakdown?.length) {
    const line =
      breakdownIndex != null
        ? breakdown[breakdownIndex]
        : breakdown.find((b) => b.method === slotId);
    if (line && lineHasUsageFee(line)) {
      return resolveLineNetAmount(line);
    }
  }
  return roundCop(gross);
}

/** Recargo total de cliente sobre un reparto base (para UI de checkout). */
export function computeClienteSurchargeDelta(
  lines: Array<{ method: string; amount: number }>,
  cfg?: PaymentMethodsConfig | null,
): number {
  let delta = 0;
  lines.forEach((line) => {
    const feeCfg = getUsageFeeForMethod(line.method, cfg);
    if (!feeCfg || feeCfg.paidBy !== "cliente") return;
    const base = roundCop(line.amount);
    if (base <= 0) return;
    const { feeAmount } = calcClienteFeeFromBase(base, feeCfg.pct);
    delta += feeAmount;
  });
  return delta;
}

/** Líneas de pago con neto resuelto (para agregación de cuadre). */
export function extractNetPaymentLinesFromVenta(venta: Venta): Array<{
  slotId: string;
  gross: number;
  net: number;
  breakdownIndex?: number;
}> {
  return extractPaymentLinesFromVenta(venta).map((ln) => ({
    slotId: ln.slotId,
    gross: ln.amount,
    net: netAmountFromPaymentLine(
      venta,
      ln.slotId,
      ln.amount,
      ln.breakdownIndex,
    ),
    breakdownIndex: ln.breakdownIndex,
  }));
}

/** Fila de tarifa para ticket de venta (web / térmica). */
export interface SaleTicketUsageFeeRow {
  methodLabel: string;
  feePct: number;
  paidBy: UsageFeePaidBy;
  grossAmount: number;
  feeAmount: number;
  netAmount: number;
}

export function formatUsageFeePctLabel(pct: number): string {
  const safe = clampPct(pct);
  return safe % 1 === 0
    ? `${safe}%`
    : `${safe.toLocaleString("es-CO", { maximumFractionDigits: 2 })}%`;
}

/** Líneas de cobro normalizadas para ticket (simple o mixto). */
export function resolveSaleTicketPaymentLines(input: {
  paymentMethod: string;
  total: number;
  paymentLines?: PaymentBreakdown[];
}): PaymentBreakdown[] {
  if (input.paymentLines?.length) {
    return input.paymentLines.filter((l) => (l.amount || 0) > 0);
  }
  if (
    !input.paymentMethod ||
    input.paymentMethod === "mixto" ||
    input.paymentMethod === "credito"
  ) {
    return [];
  }
  return [{ method: input.paymentMethod, amount: roundCop(input.total) }];
}

/** Filas con tarifa para imprimir en ticket de venta. */
export function buildSaleTicketUsageFeeRows(
  lines: PaymentBreakdown[],
  resolveLabel: (methodId: string, snapshotLabel?: string) => string,
): SaleTicketUsageFeeRow[] {
  return lines.filter(lineHasUsageFee).map((line) => ({
    methodLabel: resolveLabel(line.method, line.methodLabel),
    feePct: line.usageFeePct as number,
    paidBy: line.usageFeePaidBy as UsageFeePaidBy,
    grossAmount: roundCop(line.amount),
    feeAmount: roundCop(line.usageFeeAmount as number),
    netAmount: roundCop(line.netAmount as number),
  }));
}

/** Recargo visible al cliente (suma de tarifas paidBy=cliente). */
export function saleTicketClienteSurchargeTotal(
  rows: SaleTicketUsageFeeRow[],
): number {
  return rows
    .filter((r) => r.paidBy === "cliente")
    .reduce((s, r) => s + r.feeAmount, 0);
}

/** Neto total que ingresa al negocio tras tarifas en esta venta. */
export function saleTicketNetIngresoTotal(
  lines: PaymentBreakdown[],
  cobradoTotal: number,
): number {
  if (lines.length === 0) return roundCop(cobradoTotal);
  return lines.reduce((s, line) => s + resolveLineNetAmount(line), 0);
}

/** Etiqueta de recargo/tarifa para ticket según método y % */
export function saleTicketUsageFeeLabel(
  row: SaleTicketUsageFeeRow,
  kind: "recargo" | "tarifa",
): string {
  const pct = formatUsageFeePctLabel(row.feePct);
  const prefix = kind === "recargo" ? "Recargo pasarela" : "Tarifa pasarela";
  return `${prefix} (${row.methodLabel} ${pct})`;
}

/** Explicación del recargo para el ticket (dinámica por fila y valores reales).
 *
 * Muestra la fórmula exacta de la venta: precio ÷ (100 % − %) = total pagado.
 * Sirve para que el cliente entienda por qué el recargo NO es un simple % del
 * precio (ej. +$214 y no +$210): si se multiplicara, la pasarela cobraría su
 * comisión también sobre la comisión y al negocio le faltaría plata.
 */
export function buildClienteSurchargeTicketNote(
  row: SaleTicketUsageFeeRow,
): string {
  const net = Math.round(row.netAmount);
  const gross = Math.round(row.grossAmount);
  const divisor = (100 - row.feePct) / 100;
  const divisorLabel = divisor.toLocaleString("es-CO", {
    maximumFractionDigits: 4,
  });
  const money = (n: number) => `$${n.toLocaleString("es-CO")}`;
  return `${money(net)} ÷ ${divisorLabel} = ${money(gross)} (redondeado). No se multiplica el precio: al dividir, la comisión queda cubierta por completo.`;
}

/** Resumen cobrado / tarifa / neto para historial y pantalla de éxito. */
export function getVentaUsageFeeSummary(venta: Venta): {
  cobrado: number;
  tarifaTotal: number;
  netoIngreso: number;
  hasTarifa: boolean;
} {
  const breakdown = venta.detalleMetodoPago?.breakdown ?? [];
  const feeLines = breakdown.filter(lineHasUsageFee);
  if (feeLines.length === 0) {
    return {
      cobrado: venta.total,
      tarifaTotal: 0,
      netoIngreso: venta.total,
      hasTarifa: false,
    };
  }
  const allLines = breakdown.filter((l) => (l.amount || 0) > 0);
  const cobradoFromBreakdown = allLines.reduce(
    (s, l) => s + resolveLineGrossAmount(l),
    0,
  );
  const tarifaTotal = feeLines.reduce((s, l) => s + (l.usageFeeAmount ?? 0), 0);
  const netoFromFeeLines = feeLines.reduce(
    (s, l) => s + resolveLineNetAmount(l),
    0,
  );
  const nonFeeLines = allLines.filter((l) => !lineHasUsageFee(l));
  const netoNonFee = nonFeeLines.reduce((s, l) => s + (l.amount || 0), 0);
  return {
    cobrado: cobradoFromBreakdown > 0 ? cobradoFromBreakdown : venta.total,
    tarifaTotal,
    netoIngreso: netoFromFeeLines + netoNonFee,
    hasTarifa: tarifaTotal > 0,
  };
}
